import { motion } from 'framer-motion';
import { Truck, MapPin, Clock, Package } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

const TEXTES = {
  fr: {
    seoTitre: 'Livraison de produits africains à Liège et en Belgique - AfriOne BD',
    seoDescription: 'Livraison à Liège (5,90 €), en périphérie (7,90 €) et partout en Belgique (14,90 €, min. 75 €). Découvrez nos zones, tarifs et délais de livraison.',
    seoMotsCles: 'livraison produits africains Liège, livraison courses africaines Belgique, épicerie africaine livraison',
    surtitre: 'Livraison',
    infosTitre: 'Informations importantes',
    banniereTitre: 'Livraison rapide & sécurisée',
    banniereSous: 'Vos produits africains livrés avec soin jusque chez vous',
    altBanniere: 'Livreur remettant une commande',
    altColis: 'Remise d’un colis en main propre',
    delaiTitre: 'Délai de livraison',
    delaiTexte: 'Livraison sous 24 à 48 h après confirmation de la commande',
    emballageTitre: 'Emballage surgelé',
    emballageTexte: 'Tous nos produits surgelés sont livrés dans des emballages isothermiques',
    suiviTitre: 'Suivi de livraison',
    suiviTexte: 'Vous recevrez un SMS ou un appel avant la livraison',
    delaiMoyen: 'Délai moyen',
  },
  en: {
    seoTitre: 'African food delivery in Liège and across Belgium - AfriOne BD',
    seoDescription: 'Delivery in Liège (€5.90), the outskirts (€7.90) and throughout Belgium (€14.90, min. €75). Discover our zones, rates and delivery times.',
    seoMotsCles: 'african food delivery Liège, african grocery delivery Belgium, african shop delivery',
    surtitre: 'Delivery',
    infosTitre: 'Good to know',
    banniereTitre: 'Fast & secure delivery',
    banniereSous: 'Your African products delivered with care to your door',
    altBanniere: 'Courier handing over an order',
    altColis: 'Parcel handed over in person',
    delaiTitre: 'Delivery time',
    delaiTexte: 'Delivered within 24 to 48 hours after the order is confirmed',
    emballageTitre: 'Frozen packaging',
    emballageTexte: 'All our frozen products are delivered in insulated packaging',
    suiviTitre: 'Delivery tracking',
    suiviTexte: 'You will receive a text message or a call before delivery',
    delaiMoyen: 'Average time',
  },
};

export default function Delivery() {
  const { t, language } = useLanguage();
  const x = TEXTES[language] || TEXTES.fr;
  
  const deliveryZones = [
    {
      icon: MapPin,
      title: t('deliveryLiege'),
      titleEn: 'Delivery in Liège',
      price: '5,90€',
      description: 'Liège centre et environs immédiats',
      descriptionEn: 'Liège center and immediate surroundings',
      color: 'bg-[#f7f5f3]',
    },
    {
      icon: Truck,
      title: t('deliveryPeriphery'),
      titleEn: 'Liège Periphery',
      price: '7,90€',
      description: 'Seraing, Herstal, Ans, Flémalle, Chaudfontaine, Grace-Hollogne',
      descriptionEn: 'Seraing, Herstal, Ans, Flémalle, Chaudfontaine, Grace-Hollogne',
      color: 'bg-accent/30',
    },
    {
      icon: Package,
      title: t('deliveryOutside'),
      titleEn: 'Outside Liège',
      price: '14,90€',
      description: 'Minimum de commande: 75€',
      descriptionEn: 'Minimum order: 75€',
      color: 'bg-primary/10',
    },
  ];
  
  return (
    <div className="min-h-screen pt-20 lg:pt-24 overflow-x-hidden" data-testid="delivery-page">
      <SEO
        title={x.seoTitre}
        description={x.seoDescription}
        keywords={x.seoMotsCles}
        canonical="https://afrionebd.eu/delivery"
      />
      {/* Header */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-secondary/30 to-primary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">{x.surtitre}</span>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading text-text_primary mt-2 tracking-tight" data-testid="delivery-title">
              {t('deliveryPolicy')}
            </h1>
            <p className="text-sm sm:text-base text-text_secondary mt-4">
              {t('deliveryTitle')}
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Delivery Image Section */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="relative rounded-3xl overflow-hidden shadow-2xl"
          >
            <img
              src="/images/livraison-banniere.jpg"
              alt={x.altBanniere}
              className="w-full h-64 lg:h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-end">
              <div className="p-6 lg:p-8">
                <h3 className="text-white text-lg sm:text-2xl lg:text-3xl font-bold mb-2">
                  {x.banniereTitre}
                </h3>
                <p className="text-white/90 text-sm lg:text-base">
                  {x.banniereSous}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Delivery Zones */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {deliveryZones.map((zone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`p-8 rounded-3xl ${zone.color}`}
                data-testid={`delivery-zone-${index}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center mb-6 shadow-sm">
                  <zone.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl font-bold font-heading text-text_primary mb-2">
                  {language === 'en' ? zone.titleEn : zone.title}
                </h3>
                <p className="text-2xl sm:text-3xl font-bold text-primary mb-4">
                  {zone.price}
                </p>
                <p className="text-text_secondary">
                  {language === 'en' ? zone.descriptionEn : zone.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Additional Info */}
      <section className="py-16 lg:py-24 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold font-heading text-text_primary tracking-tight mb-4 sm:mb-6">
                {x.infosTitre}
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Clock className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-text_primary">{x.delaiTitre}</h4>
                    <p className="text-text_secondary">{x.delaiTexte}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Package className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-text_primary">{x.emballageTitre}</h4>
                    <p className="text-text_secondary">{x.emballageTexte}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Truck className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-text_primary">{x.suiviTitre}</h4>
                    <p className="text-text_secondary">{x.suiviTexte}</p>
                  </div>
                </li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="/images/livraison-colis.jpg"
                alt={x.altColis}
                className="w-full rounded-3xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-lg">
                <p className="text-2xl sm:text-3xl font-bold text-primary">24-48h</p>
                <p className="text-sm text-text_secondary">{x.delaiMoyen}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
